import { appType } from "@/shared/lib/appType";

type CloseHandler = () => void;

const overlayCloseHandlers: CloseHandler[] = [];
let stepBackHandler: (() => void) | null = null;

/**
 * native back 5단계 계약(BRIDGE_PROTOCOL §1.7 / FRONTEND_INTEGRATION_GUIDE §2.5)의 FE 측 어댑터.
 * native 는 popstate 를 발생시키지 않는다 — FE 가 상태를 push 하면 native 가 back 입력 시
 * window.closeOverlay() / window.stepBack() 을 evaluateJavaScript 로 호출한다.
 * 플랫폼 비대칭(AOS string/JSON 인자 vs iOS postMessage payload)은 이 파일에서 흡수한다.
 */
const pushOverlayOpen = (isOpen: boolean): void => {
  const platform = appType();
  if (platform === "Android") {
    window.maketrend?.setOverlayOpen?.(isOpen);
    return;
  }
  if (platform === "iOS") {
    window.webkit?.messageHandlers?.setOverlayOpenHandler?.postMessage(isOpen);
  }
};

const pushStepNavigationRequired = (required: boolean): void => {
  const platform = appType();
  if (platform === "Android") {
    window.maketrend?.setStepNavigationRequired?.(required);
    return;
  }
  if (platform === "iOS") {
    window.webkit?.messageHandlers?.setStepNavigationRequiredHandler?.postMessage(required);
  }
};

/** 라우트별 종료 확인 경로 등록 (exact match, native 가 최대 32개 cap). 저수준 프리미티브 — 레지스트리 경유로 호출한다. */
export const setExitConfirmationPaths = (paths: string[]): void => {
  const platform = appType();
  if (platform === "Android") {
    window.maketrend?.setExitConfirmationPaths?.(JSON.stringify(paths));
    return;
  }
  if (platform === "iOS") {
    window.webkit?.messageHandlers?.setExitConfirmationPathsHandler?.postMessage(paths);
  }
};

// ── exitConfirmationPaths 레지스트리 ────────────────────────────────────────
// 정적 루트 baseline(App.tsx) + 화면별 동적 경로(refcount)를 union 해 AOS 로만 push 한다.
// iOS 는 exitPath 를 쓰지 않는다(#271, 사유는 App.tsx) — iOS 종단 화면의 back 차단은 useExitOnBack 의 no-op stepBack.
const baseExitPaths = new Set<string>();
const dynamicExitPathCounts = new Map<string, number>();

const repushExitPaths = (): void => {
  if (appType() !== "Android") return; // iOS/Web 제외(#271) — 어느 경로로도 native push 0회
  const union = [...baseExitPaths, ...dynamicExitPathCounts.keys()];
  setExitConfirmationPaths(union);
};

/** 정적 루트 baseline 등록 (App.tsx 가 1회 호출; 재호출 시 교체 후 재push). */
export const setBaseExitConfirmationPaths = (paths: string[]): void => {
  baseExitPaths.clear();
  paths.forEach((p) => baseExitPaths.add(p));
  repushExitPaths();
};

/**
 * 화면이 활성인 동안 자기 경로를 종료 확인 경로에 추가한다 (refcount — 아래 acquireOverlay 와 동형).
 * 완료 sub-state 처럼 라우트를 폼 스텝과 공유하는 화면이 "그 상태 동안만" exitPath 이게 하려는 용도.
 * 동일 path 다중 acquire(짧은 동시 마운트·StrictMode dev 이중 호출)에도 마지막 release 에서만 제거된다.
 * 반환 release 는 idempotent.
 */
export const acquireExitPath = (path: string): (() => void) => {
  dynamicExitPathCounts.set(path, (dynamicExitPathCounts.get(path) ?? 0) + 1);
  repushExitPaths();
  let released = false;
  return () => {
    if (released) return;
    released = true;
    const next = (dynamicExitPathCounts.get(path) ?? 1) - 1;
    if (next <= 0) dynamicExitPathCounts.delete(path);
    else dynamicExitPathCounts.set(path, next);
    repushExitPaths();
  };
};

/**
 * 오버레이 열림을 native 에 알리고 back 닫기 핸들러를 스택에 등록한다.
 * 반환된 release 를 닫힘/언마운트 시 호출 — refcount 0 이 되면 setOverlayOpen(false).
 * setOverlayOpen 은 열린 오버레이 수가 0→1 / 1→0 으로 바뀔 때만 push 된다.
 */
export const acquireOverlay = (close: CloseHandler): (() => void) => {
  overlayCloseHandlers.push(close);
  if (overlayCloseHandlers.length === 1) pushOverlayOpen(true);

  let released = false;
  return () => {
    if (released) return;
    released = true;
    const index = overlayCloseHandlers.indexOf(close);
    if (index > -1) overlayCloseHandlers.splice(index, 1);
    if (overlayCloseHandlers.length === 0) pushOverlayOpen(false);
  };
};

/**
 * step 흐름 back 핸들러 등록(단일 슬롯) — useStepBackHandler 가 호출. 등록 중엔 setStepNavigationRequired(true) 가
 * push 돼 native back 이 window.stepBack 을 호출한다.
 */
export const registerStepBackHandler = (handler: () => void): (() => void) => {
  stepBackHandler = handler;
  pushStepNavigationRequired(true);

  return () => {
    if (stepBackHandler !== handler) return;
    stepBackHandler = null;
    pushStepNavigationRequired(false);
  };
};

/**
 * 네이티브 back 계약(overlay·stepNav)을 강제 리셋한다.
 * 전체페이지 네비게이션(카드결제 `window.location.assign` 등)은 React 언마운트를 거치지 않아
 * acquireOverlay/registerStepBackHandler 의 cleanup(= false push)이 실행되지 않는다 →
 * native 가 `setOverlayOpen(true)`/`setStepNavigationRequired(true)` 를 상태로 계속 물고 있어
 * 콜드스타트 복귀 후에도 back 이 `window.closeOverlay()`/`window.stepBack()` no-op 으로 영구 소비된다.
 * 언로드 직전(pagehide, best-effort)과 부팅 시(확정 복구) 이 함수로 FE 싱글턴을 비우고 native 에 false 를 push 한다.
 * exitConfirmationPaths 축은 건드리지 않는다 — App 이 baseline 을 재등록한다.
 */
export const resetBackContract = (): void => {
  overlayCloseHandlers.length = 0;
  stepBackHandler = null;
  pushOverlayOpen(false);
  pushStepNavigationRequired(false);
};

// native 가 evaluateJavaScript 로 호출하는 전역 콜백 — 모듈 로드 시 1회 직접 할당.
// ES module 지역 함수는 native 가 찾지 못한다 (globalSnsLoginCallback 과 동일 원리).
// native 측에 typeof 가드가 있어 핸들러 미등록 상태의 호출은 안전한 no-op 이다.
window.closeOverlay = () => {
  overlayCloseHandlers[overlayCloseHandlers.length - 1]?.();
};
window.stepBack = () => {
  stepBackHandler?.();
};
